import Bugsnag from '@bugsnag/js'
import omitBy from 'lodash/omitBy'
import qs from 'qs'
import { Dispatch } from 'react'
import { FormikHelpers } from 'formik'
import { useRouter } from 'next/router'

import ShippingAddressType from 'server/enums/ShippingAddressType'
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
} from 'checkout/containers/CheckoutStateReducer'
import { Address } from 'shared/types/Address'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { Order } from 'shared/types/Order'
import { addressErrorFieldMap } from 'shared/addressValidations'
import { createOrderBodyPayload } from 'checkout/utils/createOrderBodyPayload'
import { isTimeoutError, timeoutMessage } from 'checkout/utils/timeout'
import { t } from 'localization'
import { useAuth } from 'shared/hooks/useAuth'
import { useCreateAddressMutation } from 'api/addressesApi'
import { useCreateGuestMutation } from 'api/usersApi'
import { useCreateOrderMutation, useUpdateOrderMutation } from 'api/ordersApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useRegisterEmailMutation } from 'api/emailRegistrationApi'
import { useSetRegionPreferencesMutation } from 'api/regionPreferencesApi'
import { useUser } from 'shared/hooks/useUser'

const TP = 'checkout.hooks.useShippingAddressRTK'

type GuestShippingAddress = Address & {
  email: string
  isNewsletterActive: boolean
}

type UseShippingAddressRTKProps = {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IErrorAction>
  order?: Partial<Order>
}

export const useShippingAddressRTK = ({ dispatch, order }: UseShippingAddressRTKProps) => {
  const orderNumber = order?.number || order?.orderNumber
  const router = useRouter()
  const isGuestCheckout = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const { logoutUser } = useAuth()
  const { currentUser } = useUser()

  const [createAddress] = useCreateAddressMutation()
  const [createGuest] = useCreateGuestMutation()
  const [createOrder] = useCreateOrderMutation()
  const [registerEmail] = useRegisterEmailMutation()
  const [setRegionPreferences] = useSetRegionPreferencesMutation()
  const [updateOrder] = useUpdateOrderMutation()

  const fallbackErrorMessage = t(
    `${TP}.requestFailure`,
    'Something went wrong.  Please try again or reach out to support@flightclub.com',
  )

  const trackError = (methodName: string, error: Error) => {
    const notifyError = new Error(error?.data?.messages?.[0] || error?.message || methodName)
    Bugsnag.notify(notifyError, (event) => {
      event.addMetadata('checkout', {
        addressId: order?.addressId,
        billingInfoId: order?.billingInfoId,
        id: order?.id,
        isInstantShip: order?.isInstantShip,
        localizedCreditCents: order?.localizedCreditCents,
        localizedFinalPriceCents: order?.localizedFinalPriceCents,
        localizedPromoCodeValueCents: order?.localizedPromoCodeValueCents,
        localizedShippingCents: order?.localizedShippingCents,
        orderNumber: order?.number,
        paymentType: order?.paymentType,
        shippingAddressInRegion: order?.shippingAddressInRegion,
        shippingServiceLevel: order?.shippingServiceLevel,
        status: order?.status,
      })
      event.addMetadata('error', {
        dataMessages: error?.data?.messages,
        file: 'useShippingAddressRTK',
        message: error?.message,
        method: methodName,
        status: error?.status,
      })
      event.addMetadata('product', {
        boxCondition: order?.product?.boxCondition,
        consigned: order?.product?.consigned,
        id: order?.product?.id,
        name: order?.product?.productTemplate?.name,
        saleStatus: order?.product?.saleStatus,
        shoeCondition: order?.product?.shoeCondition,
        size: order?.product?.size,
        slug: order?.product?.slug || order?.product?.productTemplate?.slug,
        variantShoeCondition: order?.product?.variantShoeCondition,
      })
      event.addMetadata('user', {
        id: currentUser?.id,
        isGuest: currentUser?.isGuest,
      })
      event.context = 'checkout'
      event.severity = 'error'
    })
  }

  const changeShippingServiceLevelRTK = async (shippingServiceLevel: string) => {
    if (shippingServiceLevel === order?.shippingServiceLevel) {
      // Selected shipping service level is same as shipping service level saved on order
      const dispatchServiceLevelUpdate = dispatch as Dispatch<IOrderSuccessAction>
      return dispatchServiceLevelUpdate({
        type: 'ShippingServiceLevelRequestSuccess',
        payload: order,
      })
    }
    dispatch({ type: 'ShippingServiceLevelRequest' })
    if (!orderNumber) {
      return dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: t(
          `${TP}.shippingServiceLevelRequestFailure`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
    }
    if (!shippingServiceLevel) {
      return dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: t(
          `${TP}.shippingServiceLevelRequestFailure2`,
          'There was an issue selecting shipping service level. Please return to the shipping address panel.',
        ),
      })
    }
    try {
      // Update order with selected shipping service level
      const body = { orderNumber, order: { shippingServiceLevel } }
      const data = await updateOrder(body).unwrap()
      dispatch({
        type: 'ShippingServiceLevelRequestSuccess',
        payload: data,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('changeShippingServiceLevelRTK', error)
      dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const connectAddressToOrderRTK = async (addressId: number) => {
    if (!addressId) {
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(
          `${TP}.addressRequestFailure1`,
          'Unable to select address, please try a different address or add a new one.',
        ),
      })
      return
    }
    if (!orderNumber) {
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(
          `${TP}.addressRequestFailure2`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
      return
    }
    if (addressId === order?.addressId) {
      // Selected shipping address is same shipping address saved on order
      const dispatchServiceLevelUpdate = dispatch as Dispatch<IOrderSuccessAction>
      return dispatchServiceLevelUpdate({
        type: 'AddressRequestSuccess',
        payload: order,
      })
    }
    dispatch({ type: 'AddressRequest' })
    try {
      // Update order with selected shipping address
      const body = { orderNumber, order: { addressId } }
      const data = await updateOrder(body).unwrap()
      dispatch({ type: 'AddressRequestSuccess', payload: data })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('connectAddressToOrderRTK', error)
      dispatch({
        type: 'AddressRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const createGuestOrderWithShippingAddressRTK = async (
    address: GuestShippingAddress,
    formikActions: FormikHelpers<GuestShippingAddress>,
  ) => {
    dispatch({ type: 'AddressRequest' })
    if (!isGuestCheckout) {
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(`${TP}.addressRequestFailure4`, 'Guest checkout is not available at this time'),
      })
    }
    const queryParams = qs.parse(router.asPath.split('?')[1])
    const { countryCode, email, isNewsletterActive } = address

    try {
      // Create guest user
      const createGuestBody = { guestEmail: email }
      await createGuest(createGuestBody).unwrap()

      if (isNewsletterActive) {
        // Register guest's email to newsletter
        const registerEmailBody = { email }
        await registerEmail(registerEmailBody).unwrap()
      }

      // Create guest user's shipping address
      const createShippingAddressBody = {
        address: omitBy(
          {
            ...address,
            addressType: ShippingAddressType.Shipping,
          },
          (x) => x === '' || x === undefined || x === null,
        ),
      }
      const shippingAddress = await createAddress(createShippingAddressBody).unwrap()

      // Create order with guest user info and shipping address
      const createOrderBody = {
        ...createOrderBodyPayload(queryParams),
        addressId: shippingAddress.id,
      }
      const guestOrder = await createOrder(createOrderBody).unwrap()

      // Save region preference for guest user
      const setRegionPreferencesBody = {
        region_preferences: { country: countryCode, locale: router.locale },
      }
      await setRegionPreferences(setRegionPreferencesBody).unwrap()

      dispatch({ type: 'AddressRequestSuccess', payload: guestOrder })
      const guestOrderNumber = guestOrder.number
      router.replace(`/checkout?orderNumber=${guestOrderNumber}`, `/checkout/${guestOrderNumber}`, {
        locale: router.locale,
        shallow: true, // this is important to prevent a full page reload
      })
    } catch (error) {
      await logoutUser()
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'AddressRequestFailure',
          payload: t(`${TP}.addressRequestFailure5`, 'Please update the fields below'),
        })
        return
      }
      trackError('createGuestOrderWithShippingAddressRTK', error)
      dispatch({
        type: 'AddressRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const createShippingAddressForOrderRTK = async (
    address: Address,
    formikActions: FormikHelpers<Address>,
  ) => {
    if (!orderNumber) {
      dispatch({
        type: 'orderRequestFailure',
        payload: t(
          `${TP}.orderRequestFailure`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
      return
    }
    dispatch({ type: 'AddressRequest' })
    try {
      // Create new shipping address
      const createAddressBody = {
        address: omitBy(
          {
            ...address,
            addressType: ShippingAddressType.Shipping,
          },
          (x) => x === '' || x === undefined || x === null,
        ),
      }
      const createdAddress = await createAddress(createAddressBody).unwrap()

      // Update order with new shipping address
      const updateOrderBody = { orderNumber, order: { addressId: createdAddress?.id } }
      const updatedOrder = await updateOrder(updateOrderBody).unwrap()
      dispatch({
        type: 'AddressRequestSuccess',
        payload: updatedOrder,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'AddressRequestFailure',
          payload: t(`${TP}.addressRequestFailure3`, 'Please update the fields below'),
        })
        return
      }
      formikActions.setFieldError(
        'address1',
        t(`${TP}.addressFieldError`, 'Please update your address'),
      )
      trackError('createShippingAddressForOrderRTK', error)
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'AddressRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  return {
    changeShippingServiceLevelRTK,
    connectAddressToOrderRTK,
    createGuestOrderWithShippingAddressRTK,
    createShippingAddressForOrderRTK,
  }
}
